import {ActionReducerMap, createFeatureSelector, createSelector} from '@ngrx/store';
import * as fromRoot from '../../reducers';
import {SearchFilter} from '../../shared/components/advanced-searchbox/advanced-searchbox.component';
import {AttributeDictionary} from '../models/attribute';
import {LoadingCollection} from '../models/product';
import {SortOrder} from '../../shared/models/magento-search-filter';
import * as fromAttributes from './attributes.reducer';
import * as fromCollection from './collection-page.reducer';
import * as fromProducts from './products.reducer';
import * as fromDetail from './detail.reducer';
import * as fromMobileSearch from './mobile-search.reducer';


export interface ProductsState {
    collection: fromCollection.State;
    products: fromProducts.State;
    attributes: fromAttributes.AttributesState;
    detail: fromDetail.State;
    mobileSearch: fromMobileSearch.State;
}

export interface State extends fromRoot.State {
    products: ProductsState;
}

export const reducers: ActionReducerMap<ProductsState> = {
    collection: fromCollection.reducer,
    products: fromProducts.reducer,
    attributes: fromAttributes.reducer,
    detail: fromDetail.reducer,
    mobileSearch: fromMobileSearch.reducer
};

export const getProductsState = createFeatureSelector<ProductsState>('products');

// start with entities state
export const getEntitiesState = createSelector(getProductsState, state => state.products);
export const getLoadingCollection = createSelector(getEntitiesState, fromProducts.getLoadingCollection);
export const getProductsIsLoading = createSelector(getLoadingCollection, loadingCollection => {
    return Object.keys(loadingCollection).map(product => loadingCollection[product]).filter(isLoading => isLoading).length > 0;
});
export const getSelectedProduct = createSelector(getEntitiesState, fromProducts.getSelectedProduct);

export const {
    selectEntities: getProductEntities
} = fromProducts.adapter.getSelectors(getEntitiesState);

export const getDetailState = createSelector(getProductsState, (state: ProductsState) => state.detail);

export const getIsDetailTextOpen = createSelector(
    getDetailState,
    fromDetail.getIsDetailTextOpen
);
export const getIsSafetyMenuOpen = createSelector(
    getDetailState,
    fromDetail.getIsSafetyMenuOpen
);
export const getIsFreeTextActive = createSelector(
    getDetailState,
    fromDetail.getIsFreeTextActive
);
export const getSelectedQuantity = createSelector(
    getDetailState,
    fromDetail.getSelectedQuantity
);

// continue with collection state
export const getCollectionState = createSelector(getProductsState, (state: ProductsState) => state.collection);
export const getIsCollectionLoading = createSelector(getCollectionState, fromCollection.getIsLoading);
export const getCurrentPage = createSelector(getCollectionState, fromCollection.getCurrentPage);
export const getPageSize = createSelector(getCollectionState, fromCollection.getPageSize);
export const getSearchFilters = createSelector(getCollectionState, fromCollection.getSearchFilters);
export const getSortOrders = createSelector(getCollectionState, fromCollection.getSortOrders);
export const getTotalProductCount = createSelector(getCollectionState, fromCollection.getTotalProductCount);

export const getCollectionIds = createSelector(getCollectionState, fromCollection.getIds);

export const getProductCollection = createSelector(
    getProductEntities,
    getCollectionIds,
    (entities, ids) => {
        return ids.map(id => entities[id]);
    }
);

export const getProductContainerById = (id: number) => {
    return createSelector(
        getProductEntities,
        getLoadingCollection,
        (entities, loadingCollection: LoadingCollection) => {
            return {
                product: entities[id],
                isLoading: id in loadingCollection && loadingCollection[id] === true
            };
        }
    );
};

export const getProductContainerByIdAndProductsIsLoading = (id: number) => {
    return createSelector(
        getProductEntities,
        getLoadingCollection,
        getProductsIsLoading,
        (entities, loadingCollection: LoadingCollection, productsIsLoading) => {
            return {
                productContainer: {
                    product: entities[id],
                    isLoading: id in loadingCollection && loadingCollection[id] === true
                },
                productsIsLoading: productsIsLoading
            };
        }
    );
};

export const getIsProductLoading = (id: number) => {
    return createSelector(
        getProductContainerById(id),
        (p) => p.isLoading
    );
};

// attributes state
export const getAttributesState = createSelector(getProductsState, (state: ProductsState) => state.attributes);
export const getAttributesInitializationState = createSelector(getAttributesState, fromAttributes.getInitializationState);

export const {
    selectEntities: getAttributeEntities,
    selectAll: getAttributes
} = fromAttributes.adapter.getSelectors(getAttributesState);

export const getAttributeByCode = (code: string) => {
    return createSelector(
        getAttributeEntities,
        (entities) => {
            return entities[code];
        }
    );
};

// combined

export const getCollectionInitializationData = createSelector(
    getCollectionState,
    getAttributesState,
    (collectionState: fromCollection.State, attributesState: fromAttributes.AttributesState) => {
        return {
            currentPage: collectionState.currentPage,
            searchFilters: collectionState.searchFilters,
            sortOrders: collectionState.sortOrders,
            attributes: attributesState.entities,
            attributeInitializationState: attributesState.initializationState
        };
    }
);

// mobile search submenu state
export const getMobileSearchState = createSelector(getProductsState, (state: ProductsState) => state.mobileSearch);

export const getDecorTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsDecorTypeOpen);
export const getProductTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsProductTypeOpen);
export const getOccasionTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsOccasionTypeOpen);
export const getPurposeTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsPurposeTypeOpenOpen);
export const getProductCategoryTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsProductCategoryTypeOpen);
export const getColorTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsColorTypeOpen);
export const getSortTypeSubMenuOpen = createSelector(getMobileSearchState, fromMobileSearch.getIsSortOpen);

export interface CollectionInitializationData {
    currentPage: number;
    searchFilters: SearchFilter[];
    sortOrders: SortOrder[];
    attributes: AttributeDictionary;
    attributeInitializationState: fromAttributes.AttributesInitializationState;
}
